import {
  CAPTURE_HOME_MS,
  jumpMs,
  OVERSHOOT_HOLD_MS,
  ROLL_LAND_MS,
  ROLL_READ_MS,
  STEP_HOP_MS,
  type LaddersBoard,
  type LaddersMoveEvent,
} from "@bing/engine-ladders";
import { cellCentre, snakeGeom, type Pt } from "./geometry";

/* ------------------------------------------------------------------ *
 * مار پله — the MOTION (card-86; round 2, batch W1: the two-phase turn).
 *
 * The engine's pacing constants (`packages/engines/ladders/src/pacing.ts`
 * — imported, never restated, so the server's bot holds and this motion
 * cannot drift apart) turned into steps:
 *
 *   a MOVE (every new `moveCount` — the die was thrown and read BEFORE it):
 *     hop    STEP_HOP_MS × path.length     one cell per pip
 *     climb  jumpMs(jump)                  straight up the ladder, or
 *     slide  jumpMs(jump)                  down the snake's own spine
 *     home   CAPTURE_HOME_MS               every hit piece slides back to
 *                                          ITS start-pad slot, together
 *                                          (the mover stands meanwhile)
 *   an OVERSHOOT throw (a new `rollCount` that cannot move):
 *     wait   ROLL_LAND_MS + ROLL_READ_MS   the die tumbles, then is read
 *     stay   OVERSHOOT_HOLD_MS             a small shake in place
 *
 * …and then the settle (MOVE_SETTLE_MS / WIN_SETTLE_MS), which is NOT
 * motion: the pieces simply stand. So a move's steps sum to
 * `moveBeatMs(move) − settle` and a stay's to `rollHoldMs(roll) −
 * MOVE_SETTLE_MS`, pinned by `test/ladders-motion.test.ts`.
 *
 * The runner (`playMotions`) is ONE finite Web Animation per moving piece,
 * all of one length, on `transform` — a compositor animation, no rAF, no
 * timers, no CSS loop — every one cancelled the moment they finish, so
 * nothing at all runs afterwards.
 * ------------------------------------------------------------------ */

export type StepKind = "wait" | "hop" | "climb" | "slide" | "home" | "stay";

export interface MotionStep {
  readonly kind: StepKind;
  readonly ms: number;
  /** The piece's centre through the step, in board units: first = start, last = end. */
  readonly path: readonly Pt[];
}

/** Before an overshoot's stay mark: the die's tumble, then a beat to read it. */
export const STAY_DELAY_MS = ROLL_LAND_MS + ROLL_READ_MS;
/** How high a hop lifts the piece mid-step, in board units. */
export const HOP_LIFT = 0.16;
/** A hop's peak scale (the piece «lifts» toward you). */
export const HOP_SCALE = 1.12;
/** How high a hit piece arcs on its way home, in board units. */
export const HOME_LIFT = 0.5;
/** An overshoot's shake, in board units either side. */
export const STAY_SHAKE = 0.09;

/**
 * The MOVER's steps from `start` (its laid-out point before the move) to `end` (its laid-out point
 * now): the walk, the climb or slide, and — when the move hit anyone — a stand while the hit
 * pieces go home. Hops go cell centre to cell centre; the last arrival lands on `end` itself.
 */
export function moveSteps(
  board: LaddersBoard,
  move: LaddersMoveEvent,
  start: Pt,
  end: Pt,
): MotionStep[] {
  const steps: MotionStep[] = [];
  let at = start;
  move.path.forEach((cell, i) => {
    const last = i === move.path.length - 1;
    const to = last && !move.jump ? end : cellCentre(cell);
    steps.push({ kind: "hop", ms: STEP_HOP_MS, path: [at, to] });
    at = to;
  });
  const jump = move.jump;
  if (jump) {
    const ms = jumpMs(jump);
    const snake = jump.kind === "snake" ? board.snakes[jump.index] : undefined;
    if (snake && snake.head === jump.from && snake.tail === jump.to) {
      // Down the snake's OWN spine — the same samples the board drew it with.
      const spine = snakeGeom(board, jump.index).spine;
      steps.push({ kind: "slide", ms, path: [at, ...spine.slice(1, -1), end] });
    } else {
      steps.push({ kind: jump.kind === "ladder" ? "climb" : "slide", ms, path: [at, end] });
    }
    at = end;
  }
  if (move.captured.length > 0) steps.push({ kind: "wait", ms: CAPTURE_HOME_MS, path: [at, at] });
  return steps;
}

/**
 * A HIT piece's steps: it stands on its cell (`from`) while the mover walks and jumps
 * (`standMs`), then slides home to its pad slot (`to`) — at the same moment as every other piece
 * the move hit.
 */
export function homeSteps(standMs: number, from: Pt, to: Pt): MotionStep[] {
  return [
    { kind: "wait", ms: standMs, path: [from, from] },
    { kind: "home", ms: CAPTURE_HOME_MS, path: [from, to] },
  ];
}

/** One piece's part in a move: its seat and its steps (every part of a move is one length). */
export interface MotionPart {
  readonly seat: number;
  readonly steps: readonly MotionStep[];
}

/**
 * A whole MOVE: the mover's steps, then one part per hit piece. `before` / `after` are every
 * seat's laid-out point before the move (the mover on `move.from`, each hit piece on its cell) and
 * now (`pieceLayout` of the positions as they stand). Every part lasts `moveMs` — the walk + the
 * jump + `CAPTURE_HOME_MS` when anyone was hit.
 */
export function movePlan(
  board: LaddersBoard,
  move: LaddersMoveEvent,
  before: readonly Pt[],
  after: readonly Pt[],
): { readonly ms: number; readonly parts: readonly MotionPart[] } {
  const start = before[move.seat];
  const end = after[move.seat];
  if (!start || !end) return { ms: 0, parts: [] };
  const mover = moveSteps(board, move, start, end);
  const ms = motionMs(mover);
  const stand = ms - (move.captured.length > 0 ? CAPTURE_HOME_MS : 0);
  const parts: MotionPart[] = [{ seat: move.seat, steps: mover }];
  for (const hit of move.captured) {
    const from = before[hit.seat];
    const to = after[hit.seat];
    if (from && to && hit.seat !== move.seat) {
      parts.push({ seat: hit.seat, steps: homeSteps(stand, from, to) });
    }
  }
  return { ms, parts };
}

/** An OVERSHOOT throw's steps for the thrower's piece at `at`: the die, then the shake in place. */
export function staySteps(at: Pt): MotionStep[] {
  const shake = (dx: number): Pt => ({ x: at.x + dx, y: at.y });
  return [
    { kind: "wait", ms: STAY_DELAY_MS, path: [at, at] },
    {
      kind: "stay",
      ms: OVERSHOOT_HOLD_MS,
      path: [
        at,
        shake(STAY_SHAKE),
        shake(-STAY_SHAKE),
        shake(STAY_SHAKE / 2),
        shake(-STAY_SHAKE / 2),
        at,
      ],
    },
  ];
}

/** The motion's whole length — the engine's beat minus the settle. */
export function motionMs(steps: readonly MotionStep[]): number {
  return steps.reduce((sum, s) => sum + s.ms, 0);
}

export interface PieceKeyframe {
  readonly x: number;
  readonly y: number;
  readonly scale: number;
  /** 0..1 through the whole motion. */
  readonly offset: number;
  /** The timing of the segment that STARTS at this frame. */
  readonly easing: string;
}

/** The steps as keyframes for ONE Web Animation (offsets strictly increase, 0 → 1). */
export function motionKeyframes(steps: readonly MotionStep[]): PieceKeyframe[] {
  const total = motionMs(steps);
  const first = steps[0]?.path[0] ?? { x: 0, y: 0 };
  const out: PieceKeyframe[] = [];
  const push = (p: Pt, t: number, easing: string, scale = 1): void => {
    const offset = total > 0 ? t / total : 1;
    const prev = out[out.length - 1];
    if (prev && offset <= prev.offset) return; // never two frames at one instant
    out.push({ x: p.x, y: p.y, scale, offset, easing });
  };
  out.push({ x: first.x, y: first.y, scale: 1, offset: 0, easing: "linear" });
  let t0 = 0;
  for (const step of steps) {
    const a = step.path[0] ?? first;
    const b = step.path[step.path.length - 1] ?? a;
    // The easing of the frame that OPENS this step is the step's own.
    const opening = out[out.length - 1];
    const easing =
      step.kind === "climb" || step.kind === "home"
        ? "ease-in-out"
        : step.kind === "hop"
          ? "ease-out"
          : "linear";
    if (opening) out[out.length - 1] = { ...opening, easing };
    if (step.kind === "hop" || step.kind === "home") {
      const lift = step.kind === "hop" ? HOP_LIFT : HOME_LIFT;
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 - lift };
      push(mid, t0 + step.ms / 2, "ease-in", HOP_SCALE);
      push(b, t0 + step.ms, "linear");
    } else if (step.kind === "slide" && step.path.length > 2) {
      // Spread the spine's samples by arc length, so the slide's speed is even.
      const seg: number[] = [];
      for (let i = 1; i < step.path.length; i += 1) {
        const p = step.path[i - 1]!;
        const q = step.path[i]!;
        seg.push(Math.hypot(q.x - p.x, q.y - p.y));
      }
      const len = seg.reduce((s, v) => s + v, 0) || 1;
      let run = 0;
      step.path.slice(1).forEach((p, i) => {
        run += seg[i]!;
        push(p, t0 + (step.ms * run) / len, "linear");
      });
    } else {
      const n = step.path.length - 1;
      step.path.slice(1).forEach((p, i) => push(p, t0 + (step.ms * (i + 1)) / n, "linear"));
    }
    t0 += step.ms;
  }
  const last = out[out.length - 1];
  if (last && last.offset !== 1) out[out.length - 1] = { ...last, offset: 1 };
  return out;
}

/** When a motion may run at all: asked to, visible, motion allowed, and the browser can. */
export function canAnimate(opts: {
  readonly animate: boolean;
  readonly hidden: boolean;
  readonly reducedMotion: boolean;
  readonly supported: boolean;
}): boolean {
  return opts.animate && !opts.hidden && !opts.reducedMotion && opts.supported;
}

/** The user asked the OS for less motion. */
export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

/** The minimum an element must offer to be animated (`Element.animate`). */
export interface Animatable {
  animate?: (frames: Keyframe[], options: KeyframeAnimationOptions) => Animation;
}

/** One element's part of a motion: its frames. */
export interface MotionTrack {
  readonly el: Animatable;
  readonly frames: Keyframe[];
}

/**
 * Play every track as ONE finite animation each, all `durationMs` long; `onDone` runs exactly
 * once — when the last one finishes, or when the returned `finish()` ends them early (a new key,
 * the app hidden, a resize, an unmount with `notify` false). Either way EVERY animation is
 * CANCELLED, so each element falls back to its own inline transform (the end state) and nothing
 * is left running. Never the `finished` promise: a `cancel()` rejects it, and an unhandled
 * rejection is a crash report.
 */
export function playMotions(
  tracks: readonly MotionTrack[],
  durationMs: number,
  onDone: () => void,
): (notify?: boolean) => void {
  let open = true;
  const anims: Animation[] = [];
  const end = (notify = true): void => {
    if (!open) return;
    open = false;
    for (const a of anims) {
      a.onfinish = null;
      a.cancel();
    }
    if (notify) onDone();
  };
  const playable = tracks.filter((t) => typeof t.el.animate === "function");
  if (playable.length === 0) {
    end(true);
    return end;
  }
  let left = playable.length;
  for (const t of playable) {
    const anim = t.el.animate!(t.frames, { duration: durationMs, easing: "linear", fill: "none" });
    anim.onfinish = () => {
      left -= 1;
      if (left === 0) end(true);
    };
    anims.push(anim);
  }
  return end;
}

/** {@link playMotions} for one element. */
export function playMotion(
  el: Animatable,
  frames: Keyframe[],
  durationMs: number,
  onDone: () => void,
): (notify?: boolean) => void {
  return playMotions([{ el, frames }], durationMs, onDone);
}
